import { FC, useContext, useMemo } from "react";
import {
  SchemaItemProps,
  useClientContext,
  useAppContext,
  useField,
  UseRowContext,
  useRows,
  UseRowsContext,
} from "@data-root/react";
import { RefRowsSchemaItem, Filter } from "@data-root/types";
import { Comment } from "./comment";
import { CreateComment } from "./create-comment";
import { Typography } from "antd";
import { ErrorView } from "@data-root/error-boundary";

const { Title } = Typography;

export const FeedPresentation: FC<SchemaItemProps<RefRowsSchemaItem>> = ({
  item,
}) => {
  const { core } = useClientContext();
  const { appSpecifier } = useAppContext();
  const { loaded, rowId } = useContext(UseRowContext)!;
  const { displayName } = useField(item);

  const filters = useMemo<Filter[]>(() => {
    if (!loaded) return [];

    return [
      {
        id: item.refField,
        type: "text",
        value: rowId,
      },
    ];
  }, [item.refField, loaded, rowId]);

  const useRefRowsContext = useRows(core, appSpecifier, item.ref, filters);
  const { displayRows: comments, error, create, _delete } = useRefRowsContext;

  const handleNewComment = async (content: string) => {
    try {
      await create({
        values: {
          content,
          author: core.user?.id,
          [item.refField]: rowId,
        },
      });
    } catch (err) {
      console.error("Failed to create comment:", err);
    }
  };

  if (error) return <ErrorView error={error} />;

  return (
    <UseRowsContext.Provider value={useRefRowsContext}>
      <div className="w-full space-y-6">
        <Title level={5}>{displayName}</Title>

        {comments.map((comment) => (
          <Comment
            key={comment.id}
            id={comment.id}
            author={{
              name: (comment.$author as any) || "User",
              avatar: comment.$avatar as any,
            }}
            content={comment.$content as any}
            createdAt={new Date()}
            bordered={false}
            onDelete={async () => {
              await _delete(comment.id);
            }}
          />
        ))}

        <CreateComment
          user={{
            name: core.user?.name || "User",
          }}
          onSubmit={handleNewComment}
          bordered={false}
        />
      </div>
    </UseRowsContext.Provider>
  );
};
